











































import {
  defineComponent,
  onMounted,
  PropType,
  ref,
  computed,
  onBeforeUnmount,
} from '@vue/composition-api';
import { Button, Toast } from '@hb/coffee';
import type { ContractListItem as ApiContractListItem } from '@/api/rights/shop-mall/types';
import { ContractTypeEnum } from '@/widgets/contract-view/type';
import { creditMallApply } from '@/api/rights/shop-mall';
import { openThirdPage } from '@/api/native/utils';
import { ubtModuleExposeNew, ubtPageButtonNew } from '@/shared/utils/ubt';
import router from '@/router';
import { VerifySceneEnum, OpenVerifyPageMode } from '@/shared/verify-credit-controller/types';
import jumpToVerifyCredit from '@/shared/verify-credit-controller';
import ContractView from '../contract-view/flatten-contract/index.vue';
import { pollingStatus } from '../installment-mall/pollingStatus';
import { InstallMallCreditStatusEnum } from '../installment-mall/types';
import { shopMallAgreementPopupClick, shopMallAgreementPopupExpose } from '../installment-mall/tianqi';

export default defineComponent({
  components: {
    ContractView,
    Button,
  },
  props: {
    /** 协议列表 */
    contractList: {
      type: Array as PropType<ApiContractListItem[]>,
      required: true,
      default: () => [],
    },
    /** 来源标识 */
    from: {
      type: String,
      default: 'finance_screening_agreement',
    },
    /** 最高可申请金额 */
    applyAmount: {
      type: String,
      default: '200000',
    },
    /** 产品ID列表 */
    productIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  emits: ['apply-success', 'apply-fail'],
  setup(props, { emit }) {
    /** 倒计时秒数 */
    const countdown = ref(5);
    /** 倒计时定时器 */
    let countdownTimer: number | null = null;
    /** 授信状态 */
    const creditStatus = ref<InstallMallCreditStatusEnum>(InstallMallCreditStatusEnum.WAIT_CREDIT);
    /** 成功跳转地址 */
    const successJumpUrl = ref<string>('');

    /** 格式化金额 */
    const formatAmount = computed(() => {
      const amount = props.applyAmount;
      if (typeof amount === 'number' || (typeof amount === 'string' && Number(amount))) {
        return Number(amount).toLocaleString();
      }
      return amount;
    });

    /** 适配协议列表数据格式 */
    const adaptedContractList = computed(() => props.contractList.map((item) => ({
      name: item.contractName,
      url: item.contractUrl,
      type: item.contentType === 1 ? ContractTypeEnum.PDF : ContractTypeEnum.HTML,
    })));

    /** 按钮是否禁用 */
    const isButtonDisabled = computed(() => (
      countdown.value > 0
      || creditStatus.value === InstallMallCreditStatusEnum.CREDIT_PROCESSING
    ));

    /** 按钮文案 */
    const buttonText = computed(() => {
      if (countdown.value > 0) {
        return `已阅读并同意协议（${countdown.value}s）`;
      }
      if (creditStatus.value === InstallMallCreditStatusEnum.CREDIT_PROCESSING) {
        return '授信中...';
      }
      if (creditStatus.value === InstallMallCreditStatusEnum.CREDIT_SUCCESS) {
        return '去使用';
      }
      return '已阅读并同意协议';
    });

    /**
     * 启动倒计时
     */
    const startCountdown = () => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
      countdown.value = 5;
      countdownTimer = window.setInterval(() => {
        countdown.value -= 1;
        if (countdown.value <= 0) {
          if (countdownTimer) {
            clearInterval(countdownTimer);
            countdownTimer = null;
          }
        }
      }, 1000);
    };

    /**
     * 轮询授信状态
     */
    const polling = pollingStatus({
      // creditTaskId,
      from: props.from,
      onStatusUpdate: (status) => {
        creditStatus.value = status;
      },
      onSuccess: async (res) => {
        // 授信成功
        if (res?.jumpUrl) {
          successJumpUrl.value = res.jumpUrl;
          emit('apply-success', res);
          openThirdPage(res.jumpUrl);
        } else {
          Toast('未能获取到跳转地址，请稍后再试');
        }
      },
      onFailed: () => {
        Toast('本次消费分期额度审核失败，保持良好信用下次再试');
        emit('apply-fail');
      },
      onTimeout: () => {
        Toast('未查询到开通结果');
      },
    });

    /**
     * 请求分期商城进件接口
     */
    const requestApply = async () => {
      try {
        creditStatus.value = InstallMallCreditStatusEnum.CREDIT_PROCESSING;

        // 调用分期商城申请接口
        const response = await creditMallApply({
          from: props.from,
          productIds: props.productIds,
        });

        if (response?.creditTaskId) {
          /** 申请成功，执行申请成功回调 */
          emit('apply-success', response);
        } else {
          Toast('申请失败，请稍后重试');
          creditStatus.value = InstallMallCreditStatusEnum.WAIT_CREDIT;
        }
      } catch (error: any) {
        /** 兼容需要补充资料的场景 */
        if (error.code === 300002) {
          jumpToVerifyCredit(VerifySceneEnum.PRE_CREDIT_FQ_SHOPPING, {
            openMode: OpenVerifyPageMode.POPUP_SHOW,
            successCb: async () => {
              requestApply();
            },
          });
          return;
        }
        Toast('申请失败，请稍后重试');
        creditStatus.value = InstallMallCreditStatusEnum.WAIT_CREDIT;
      }
    };

    /**
     * 处理申请按钮点击
     */
    const handleApply = async () => {
      ubtPageButtonNew(router.currentRoute.meta?.pageId as string, shopMallAgreementPopupClick, {
        from: props.from,
        productIds: props.productIds,
      });
      // 如果在倒计时或授信中，不处理
      if (isButtonDisabled.value) {
        return;
      }

      // 如果已经授信成功，跳转
      if (creditStatus.value === InstallMallCreditStatusEnum.CREDIT_SUCCESS) {
        if (successJumpUrl.value) {
          openThirdPage(successJumpUrl.value);
        } else {
          Toast('未能获取到跳转地址，请稍后再试');
        }
        return;
      }

      // 发起授信申请
      await requestApply();
    };

    onMounted(() => {
      // 组件挂载后启动倒计时
      startCountdown();
      ubtModuleExposeNew(router.currentRoute.meta?.pageId as string, shopMallAgreementPopupExpose, {
        from: props.from,
        productIds: props.productIds,
      });
    });

    onBeforeUnmount(() => {
      // 清理定时器
      if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
      // 停止轮询
      polling.cancel();
    });

    return {
      countdown,
      creditStatus,
      formatAmount,
      adaptedContractList,
      isButtonDisabled,
      buttonText,
      handleApply,
      InstallMallCreditStatusEnum,
    };
  },
});
